<template>
  <div class="currency-input-wrap">
    <span class="currency-prefix" aria-hidden="true">R$</span>
    <input
      ref="inputRef"
      :id="id"
      type="text"
      inputmode="decimal"
      :value="displayValue"
      :placeholder="placeholder"
      :aria-label="ariaLabel"
      :aria-invalid="invalid"
      class="currency-input"
      @input="onInput"
      @blur="onBlur"
      @focus="onFocus"
    />
  </div>
</template>

<script>
export default {
  name: 'CurrencyInput',
  props: {
    modelValue: {
      type: [Number, String],
      default: null,
    },
    id: { type: String, default: '' },
    placeholder: { type: String, default: '0,00' },
    ariaLabel: { type: String, default: '' },
    invalid: { type: Boolean, default: false },
  },
  emits: ['update:modelValue'],
  data() {
    return {
      focused: false,
      /** While focused: raw string shown in input. No reformatting so cursor stays put. */
      inputStr: '',
    };
  },
  computed: {
    displayValue() {
      if (this.focused) {
        return this.inputStr;
      }
      const num = this.numberValue;
      if (num === null || num === '' || isNaN(num)) return '';
      return this.formatBrl(num);
    },
    numberValue() {
      const v = this.modelValue;
      if (v === null || v === undefined || v === '') return null;
      const n = Number(v);
      return isNaN(n) ? null : n;
    },
  },
  methods: {
    formatBrl(num) {
      return Number(num).toLocaleString('pt-BR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
    parseBrl(str) {
      if (!str || typeof str !== 'string') return null;
      const s = str.replace(/\s/g, '').trim();
      if (s === '') return null;
      if (s === ',' || s === '.') return null;
      const hasComma = s.includes(',');
      const parts = s.split(',');
      let numStr;
      if (hasComma && parts.length === 2) {
        numStr = parts[0].replace(/\./g, '') + '.' + parts[1].replace(/\D/g, '').slice(0, 2);
      } else if (hasComma && parts.length === 1) {
        numStr = parts[0].replace(/\./g, '');
      } else {
        numStr = s.replace(/\./g, '');
      }
      const n = parseFloat(numStr);
      return isNaN(n) ? null : n;
    },
    onFocus() {
      this.focused = true;
      const num = this.numberValue;
      this.inputStr = num !== null ? this.formatBrl(num) : '';
    },
    onBlur() {
      this.focused = false;
      const parsed = this.parseBrl(this.inputStr);
      this.$emit('update:modelValue', parsed !== null ? parsed : null);
      this.inputStr = '';
    },
    onInput(e) {
      this.inputStr = e.target.value;
    },
  },
};
</script>

<style scoped>
.currency-input-wrap {
  display: flex;
  align-items: center;
  width: 100%;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-sm);
  background: var(--color-surface);
  transition: border-color 0.2s, box-shadow 0.2s;
}

.currency-input-wrap:focus-within {
  border-color: var(--color-border-focus);
  box-shadow: 0 0 0 3px var(--color-accent-soft);
}

.currency-prefix {
  padding: 10px 0 10px 12px;
  font-size: 0.95rem;
  color: var(--color-text-secondary);
  font-weight: 500;
}

.currency-input {
  flex: 1;
  min-width: 0;
  border: none;
  padding: 10px 12px 10px 6px;
  font-family: inherit;
  font-size: 0.95rem;
  color: var(--color-text);
  background: transparent;
}

.currency-input:focus {
  outline: none;
  box-shadow: none;
}

.currency-input::placeholder {
  color: var(--color-text-muted);
}
</style>
